/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { request } from '../request'
import type { RequestResponse } from '../request'
import {
  AsrModel,
  AsrRes,
  AssetAddParams,
  AssetCreate,
  ProjectAssetsModel,
  QueryAsrResModel,
  Tool,
} from './type'
import type {
  GetPreUploadParams,
  GetAssetProgressParams,
  GetProjectAssetsParams,
  GetAssetListsParams,
  SearchAssetParams,
  ProjectAssets,
  PreUploadModel,
  TransProgress,
  ListAssets,
  ListAssists,
  FontModel,
  PresetFontModel,
  AssetList,
  AssetAdd,
  AssetCreateParams,
  ReplaceAssetParams,
  FragmentParams,
  FragmentTagsModel,
  VeditorTemplateModel,
  FragmentListModel,
} from './type'
import { AnimationType } from '@ve-pro/enums/animation'
import { AssetSource } from '@ve-pro/enums/asset'
import type {
  ProjectInfoModel,
  ProjectModel,
  ProjectSegmentModel,
} from '../project/type'
import type { SegmentFragmentsResource } from '../template/type'

// 文件预上传
export const getPreUploadList = (params?: GetPreUploadParams) =>
  request.get<RequestResponse<PreUploadModel>>('veditor/asset/upload', {
    params,
  })

// 文件转码进度
export const getAssetProgress = (params?: GetAssetProgressParams) =>
  request.get<RequestResponse<TransProgress[]>>('veditor/asset/progress', {
    params,
  })

// 项目资源列表
export const getProjectAssets = (params?: GetProjectAssetsParams) =>
  request.get<RequestResponse<ProjectAssets>>('veditor/project/assets', {
    params,
  })

// 删除项目资源
export const deleteAssets = (pid: number, aid: number) =>
  request.post<RequestResponse>(
    '/veditor/project/del-asset',
    {
      pid,
      aid,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )

// 主标签和资源分类列表
export const getListAsset = (
  type: number,
  params?: Partial<SearchAssetParams>,
) =>
  request.get<RequestResponse<ListAssets>>(
    `common/asset-tag/assets?type=${type}&tool=${Tool}`,
    {
      params,
    },
  )

// 搜索-辅助标签
export const getListAssist = (type: number) =>
  request.get<RequestResponse<ListAssists>>(
    `common/asset-tag/assists?type=${type}&tool=${Tool}`,
  )

// 标签值资源分类列表
export const getFontList = () =>
  request.get<RequestResponse<{ list: null | FontModel[] }>>(
    'common/font/list?tool=2',
  )

// 文本预设样式
export const getPresetFonts = (params?: { page_size: number }) =>
  request.get<RequestResponse<{ list: null | PresetFontModel[] }>>(
    `common/asset/preset-fonts?tool=${Tool}`,
    {
      params,
    },
  )

// 标签组资源列表
const types: Record<AnimationType, number> = {
  [AnimationType.In]: 1,
  [AnimationType.Out]: 2,
  [AnimationType.Inout]: 3,
  [AnimationType.Loop]: 4,
}
export const getAssetList = (
  params: GetAssetListsParams,
  data?: Partial<SearchAssetParams>,
) => {
  const copied: any = { ...params }
  if (typeof params.cartoon_types !== 'undefined') {
    copied.cartoon_types = params.cartoon_types.map((t) => types[t]).join(',')
  }

  return request.get<RequestResponse<AssetList>>(
    `common/asset/list?tool=${Tool}`,
    {
      params: copied,
      data,
    },
  )
}

/** **************/
// 1、公共库、资产库支持批量
// 参数和输出要改下  参数：id => ids 输出 asset (object)  =>  assets (list)
export const getAsset = (data: AssetAddParams, source?: AssetSource) => {
  let url: string
  // 来自资产库
  if (typeof source !== 'undefined' && source === AssetSource.Asset) {
    url = 'veditor/asset/choose'
  } else {
    url = 'veditor/asset/add'
  }

  return request.post<RequestResponse<AssetAdd>>(
    url,
    {
      ...data,
      ids: data.ids,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )
}

export const getFragment = (params: {
  fragment_id: SegmentFragmentsResource['fragment_id']
  project_id: ProjectModel['id']
}) => {
  return request.get<RequestResponse<ProjectInfoModel>>(
    'veditor/fragment/choose',
    {
      params,
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )
}

// 二次包装

// 公共资源添加到轨道
export const getAssetAdd = (data: AssetAddParams) =>
  getAsset(data, AssetSource.Public)

// 资产库添加到轨道
export const getAssetChoose = (data: AssetAddParams) =>
  getAsset(data, AssetSource.Asset)
/** **************/

// 资源收藏
export const assetFavorite = (data: {
  add_type: number
  type: number
  asset_id: number
}) =>
  request.post<RequestResponse>(
    'common/asset/favorite',
    Object.assign(data, { tool: Tool }),
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )

// 查看收藏夹列表
export const getAssetFavorites = (
  params?: GetAssetListsParams,
  data?: Partial<SearchAssetParams>,
) =>
  request.get<RequestResponse<AssetList>>(
    `common/asset/favorites?tool=${Tool}`,
    {
      params,
      data,
    },
  )

// 分离视频的音频资源
export const separateVideoAudioAsset = (data: {
  project_id: ProjectModel['id']
  asset_id: ProjectSegmentModel['asset_id']
}) =>
  request.post<RequestResponse<ProjectAssetsModel>>(
    'veditor/asset/separate',
    data,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )

// 更新资源分镜状态
export const updateAssetShotStatus = (data: {
  shot_status: ProjectAssetsModel['shot_status']
  asset_id: ProjectAssetsModel['asset_id']
  shot_frame_ids: string
  shot_task_id: ProjectAssetsModel['shot_task_id']
}) =>
  request.post<RequestResponse<ProjectAssetsModel>>(
    'veditor/asset/update-shot-status',
    data,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )

// 批量创建资源
export const createAsset = (data: AssetCreateParams) =>
  request.post<RequestResponse<AssetCreate>>('veditor/asset/create', data, {
    headers: {
      'Content-Type': 'application/json',
    },
  })

// 创建语音、视频转字幕任务
export const createAsrRequest = (data: { batch: AsrModel[]; source: string }) =>
  request.post<RequestResponse<AsrRes | null>>('ai/asr', data, {
    headers: {
      'Content-Type': 'application/json',
    },
  })

export const queryTaskRequest = (params?: { ids: AsrRes['ids'] }) =>
  request.get<RequestResponse<QueryAsrResModel[] | null>>(`async/query`, {
    params: {
      ids: params?.ids.toString(),
    },
  })

// 视频编辑器转码重试
export const videoRetryTranscode = (asset_id: string) =>
  request.get<RequestResponse<string>>(`veditor/asset/retry-transcode`, {
    params: {
      asset_id,
    },
  })
// 替换资源
export const replaceAsset = (data: ReplaceAssetParams) =>
  request.post<RequestResponse<ProjectAssetsModel>>(
    'veditor/asset/replace-asset',
    data,
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  )

// 内容模版列表
export const getVeditorTemplateList = () =>
  request.get<RequestResponse<VeditorTemplateModel[] | null>>(
    'veditor/template/tag-list?limit=10',
  )

// 模版详情
export const getVeditorTemplateDetail = (id: number) =>
  request.get<RequestResponse<string>>('veditor/template/detail', {
    params: {
      id,
    },
  })

// 片段列表
export const getVeditorFragmentList = (params?: FragmentParams) =>
  request.get<RequestResponse<FragmentListModel[] | null>>(
    'veditor/fragment/list',
    {
      params: {
        ...params,
        ratios: params?.ratios?.join(','),
      },
    },
  )

// 片段标签
export const getVeditorFragmentTags = () =>
  request.get<RequestResponse<{ tags: null | FragmentTagsModel[] }>>(
    'veditor/fragment/tags',
  )
